import React, { FC, useCallback, useState } from 'react';
import { Cancel } from '@mui/icons-material';
import { CircularProgress, Dialog, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import API from 'api/API';
import Button from 'components/Button';
import InputField from 'components/InputField';
import { useLanguage } from 'context/AppContext';
import * as Styles from './DeleteUserModal.styles';

interface DeleteUserModalProps {
  open: boolean;
  onClose: () => void;
  user: {
    id: string;
    email: string;
  };
  onSubmit: () => void;
}

const DeleteUserModal: FC<DeleteUserModalProps> = ({ open, onClose, user, onSubmit: onSubmitExternal }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [disabled, setDisabled] = useState(true);
  const [
    {
      UserManagement: {
        DeleteModal: { title, placeholder, submit, body, errorMessage, successMessage },
      },
    },
  ] = useLanguage();

  const onChange = useCallback(
    (email: string) => {
      setEmail(email);
      setDisabled(email !== user.email);
    },
    [user],
  );

  const onSubmit = useCallback(() => {
    setLoading(true);
    API.userManagement
      .deleteUser(user.id)
      .then(() => toast.success(successMessage))
      .catch(error => toast.error(errorMessage + error))
      .finally(() => onSubmitExternal());
  }, [user, onSubmitExternal]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <Styles.Container>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <IconButton sx={{ position: 'absolute', top: '1rem', right: '1rem' }} onClick={onClose}>
              <Cancel />
            </IconButton>
            <h2>{title}</h2>
            <Styles.Message>{body.replace('{{email}}', user.email)}</Styles.Message>
            <Styles.InputContainer>
              <InputField
                fullWidth
                variant="outlined"
                onChange={event => onChange(event.target.value)}
                {...{ placeholder, email }}
              />
            </Styles.InputContainer>
            <Button onClick={onSubmit} fullWidth disabled={disabled}>
              {submit}
            </Button>
          </>
        )}
      </Styles.Container>
    </Dialog>
  );
};

export default DeleteUserModal;
